import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { MetabaseApi } from "../../services/api/multi/metabase";

const SCRIPT_ID = "metabase-embed-script";

function normalizeMetabaseSiteUrl(url) {
  const trimmed = String(url || "")
    .trim()
    .replace(/\/$/, "");
  if (!trimmed) return "https://metabase.lage-beauty.de";
  if (trimmed.startsWith("http://")) {
    return `https://${trimmed.slice("http://".length)}`;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function applyMetabaseConfig(siteUrl, themePreset = "dark") {
  // Match Metabase's published Guest Embed snippet.
  window.defineMetabaseConfig =
    window.defineMetabaseConfig ||
    function defineMetabaseConfig(config) {
      window.metabaseConfig = config;
    };
  window.defineMetabaseConfig({
    theme: { preset: themePreset === "light" ? "light" : "dark" },
    isGuest: true,
    instanceUrl: siteUrl,
  });
}

function loadEmbedScript(siteUrl) {
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    if (existing.dataset.siteUrl === siteUrl) {
      return existing.dataset.loaded === "1"
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener(
              "error",
              () => reject(new Error("Failed to load Metabase embed script.")),
              { once: true },
            );
          });
    }
    existing.remove();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.dataset.siteUrl = siteUrl;
    script.src = `${siteUrl}/app/embed.js`;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "1";
      resolve();
    };
    script.onerror = () =>
      reject(new Error("Failed to load Metabase embed script."));
    document.head.appendChild(script);
  });
}

/**
 * Mount Metabase's custom element via DOM APIs so React never applies an
 * inline `style` attribute (Metabase CSP blocks style-src unsafe-inline).
 */
function MetabaseDashboardElement({ token }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !token) return undefined;

    host.replaceChildren();
    const el = document.createElement("metabase-dashboard");
    el.setAttribute("token", token);
    el.setAttribute("with-title", "true");
    el.setAttribute("with-downloads", "true");
    host.appendChild(el);

    return () => {
      host.replaceChildren();
    };
  }, [token]);

  return (
    <Box
      ref={hostRef}
      className="metabase-dashboard-host"
      sx={{
        width: "100%",
        height: "100%",
        "& metabase-dashboard": {
          display: "block",
          width: "100%",
          height: "100%",
        },
      }}
    />
  );
}

export default function MetabaseEmbed() {
  const [embed, setEmbed] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await MetabaseApi.getEmbedToken();
      const siteUrl = normalizeMetabaseSiteUrl(data.site_url);
      applyMetabaseConfig(siteUrl, data.theme || "dark");
      await loadEmbedScript(siteUrl);
      setEmbed({ ...data, site_url: siteUrl });
    } catch (requestError) {
      setEmbed(null);
      setError(
        requestError.response?.data?.detail ||
          requestError.message ||
          "Failed to load Metabase dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  useEffect(() => {
    if (!embed?.expires_in) return undefined;
    const refreshAfterMs = Math.min(
      Math.max(30, embed.expires_in - 60) * 1000,
      24 * 60 * 60 * 1000,
    );
    const timeout = window.setTimeout(refreshToken, refreshAfterMs);
    return () => window.clearTimeout(timeout);
  }, [embed?.expires_in, refreshToken]);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Analytics dashboard</Typography>
        <Tooltip title="Refresh embed token">
          <span>
            <IconButton
              size="small"
              onClick={refreshToken}
              disabled={loading}
              aria-label="Refresh Metabase embed"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading && !embed ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 720,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "background.default",
          }}
        >
          <MetabaseDashboardElement key={embed.token} token={embed.token} />
        </Box>
      )}
    </Paper>
  );
}
